import React, { useState, useEffect, useCallback } from "react";
import { RefreshCw, Plus, X, TrendingUp, TrendingDown } from "lucide-react";
import { OPTIONS_URLS } from "../../api/OptionsUrls";
import { AI_TRADER_URLS } from "../../api/AiTraderUrls";
import { get, post } from "../../services/smartApiService";

// ─── types ───────────────────────────────────────────────────────────────────
interface FiiDii {
  net_value: number | null;
  mtd_net: number | null;
  prev_day_net: number | null;
  buy_value: number | null;
  sell_value: number | null;
}
interface VixData { value: number | null; change_percent: number | null; }
interface MarketDailyData {
  date: string | null;
  fii: FiiDii;
  dii: FiiDii;
  india_vix: VixData;
}
interface GlobalMarket {
  sgx_nifty: number | null;
  sgx_nifty_change_pct: number | null;
  dow_futures: number | null;
  dow_futures_change_pct: number | null;
  nasdaq_futures: number | null;
  nasdaq_futures_change_pct: number | null;
  gold: number | null;
  gold_change_pct: number | null;
  crude_oil: number | null;
  crude_oil_change_pct: number | null;
  usd_inr: number | null;
  usd_inr_change: number | null;
  global_sentiment: string | null;
  global_score: number | null;
}
const initialForm = {
  date: new Date().toISOString().split('T')[0],
  fii_buy_value: '', fii_sell_value: '',
  dii_buy_value: '', dii_sell_value: '',
  india_vix: '',
};

// ─── helpers ─────────────────────────────────────────────────────────────────
const fmt = (v: number | null | undefined, prefix = '') => {
  if (v == null) return '--';
  const abs = Math.abs(v);
  return abs >= 1000 ? `${prefix}${(v / 1000).toFixed(1)}K Cr` : `${prefix}${v.toFixed(0)} Cr`;
};
const fmtPct = (v: number | null | undefined) => {
  if (v == null) return null;
  return `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
};
const clr = (v: number | null | undefined, invert = false) => {
  if (v == null) return '#666';
  const pos = invert ? v < 0 : v >= 0;
  return pos ? '#26a65b' : '#e74c3c';
};

// ─── Ticker item ─────────────────────────────────────────────────────────────
function Chip({
  label, value, pct, invert = false,
}: { label: string; value: string; pct?: string | null; invert?: boolean }) {
  const color = pct != null ? clr(parseFloat(pct), invert) : '#aaa';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '0 12px', borderRight: '1px solid #1a1a2a', flexShrink: 0 }}>
      <span style={{ color: '#555', fontSize: '0.6rem', fontWeight: 600, letterSpacing: 0.5 }}>{label}</span>
      <span style={{ color: '#bbb', fontSize: '0.68rem', fontWeight: 700 }}>{value}</span>
      {pct && (
        <span style={{ color, fontSize: '0.6rem', fontWeight: 600 }}>{pct}</span>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const MarketInfoLayer: React.FC = () => {
  const [daily, setDaily] = useState<MarketDailyData | null>(null);
  const [global, setGlobal] = useState<GlobalMarket | null>(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(() => {
    setLoading(true);
    // FII/DII/VIX
    get(OPTIONS_URLS.MARKET_DAILY_DATA(undefined, false), { showLoading: false, requiresAuthorization: false }).subscribe({
      next: (r: any) => r?.data && setDaily(r.data),
      error: () => {},
    });
    // Global market from AI Trader
    get(AI_TRADER_URLS.GLOBAL_MARKET, { showLoading: false, handleError: () => {} }).subscribe({
      next: (r: any) => { setGlobal(r?.data ?? null); setLoading(false); },
      error: () => setLoading(false),
    });
  }, []);

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [fetchAll]);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    post(OPTIONS_URLS.SAVE_MARKET_DAILY_DATA, {
      date: form.date,
      fii_buy_value: form.fii_buy_value ? +form.fii_buy_value : null,
      fii_sell_value: form.fii_sell_value ? +form.fii_sell_value : null,
      dii_buy_value: form.dii_buy_value ? +form.dii_buy_value : null,
      dii_sell_value: form.dii_sell_value ? +form.dii_sell_value : null,
      india_vix: form.india_vix ? +form.india_vix : null,
    }, { showLoading: false, requiresAuthorization: false }).subscribe({
      next: () => { setSaving(false); setShowModal(false); setForm(initialForm); fetchAll(); },
      error: (err: any) => { setSaving(false); alert(err?.message || 'Failed to save'); },
    });
  };

  const sentimentColor = { bullish: '#26a65b', bearish: '#e74c3c', neutral: '#f39c12' }[global?.global_sentiment || 'neutral'] || '#666';

  return (
    <>
      <div style={{
        background: '#07070f',
        borderBottom: '1px solid #14141f',
        height: 34,
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        flexShrink: 0,
      }}>
        {/* Date */}
        <div style={{ padding: '0 12px', borderRight: '1px solid #1a1a2a', flexShrink: 0 }}>
          <span style={{ color: '#444', fontSize: '0.6rem', fontWeight: 600 }}>
            {daily?.date
              ? new Date(daily.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
              : new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
          </span>
        </div>

        {/* Scrollable ticker */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', overflowX: 'auto', scrollbarWidth: 'none' }}>

          {/* FII */}
          <Chip
            label="FII"
            value={fmt(daily?.fii?.net_value)}
            pct={daily?.fii?.net_value != null ? (daily.fii.net_value >= 0 ? `+${daily.fii.net_value.toFixed(0)}` : `${daily.fii.net_value.toFixed(0)}`) : null}
          />

          {/* DII */}
          <Chip
            label="DII"
            value={fmt(daily?.dii?.net_value)}
            pct={daily?.dii?.net_value != null ? (daily.dii.net_value >= 0 ? `+${daily.dii.net_value.toFixed(0)}` : `${daily.dii.net_value.toFixed(0)}`) : null}
          />

          {/* VIX */}
          <Chip
            label="VIX"
            value={daily?.india_vix?.value?.toFixed(2) || '--'}
            pct={fmtPct(daily?.india_vix?.change_percent)}
            invert
          />

          {/* FII MTD */}
          <Chip label="FII MTD" value={fmt(daily?.fii?.mtd_net)} pct={null} />

          {/* DII MTD */}
          <Chip label="DII MTD" value={fmt(daily?.dii?.mtd_net)} pct={null} />

          {/* divider */}
          <div style={{ width: 1, height: 18, background: '#222', margin: '0 4px', flexShrink: 0 }} />

          {/* Global market data — auto from AI Trader */}
          {global ? (
            <>
              <Chip label="SGX" value={global.sgx_nifty?.toLocaleString('en-IN') || '--'} pct={fmtPct(global.sgx_nifty_change_pct)} />
              <Chip label="DOW" value={global.dow_futures != null ? global.dow_futures.toLocaleString('en-US') : '--'} pct={fmtPct(global.dow_futures_change_pct)} />
              <Chip label="NASDAQ" value={global.nasdaq_futures != null ? global.nasdaq_futures.toLocaleString('en-US') : '--'} pct={fmtPct(global.nasdaq_futures_change_pct)} />
              <Chip label="GOLD" value={global.gold != null ? `$${global.gold.toLocaleString('en-US')}` : '--'} pct={fmtPct(global.gold_change_pct)} />
              <Chip label="CRUDE" value={global.crude_oil != null ? `$${global.crude_oil.toFixed(1)}` : '--'} pct={fmtPct(global.crude_oil_change_pct)} invert />
              <Chip label="USD/INR" value={global.usd_inr != null ? global.usd_inr.toFixed(2) : '--'} pct={global.usd_inr_change != null ? (global.usd_inr_change >= 0 ? `+${global.usd_inr_change.toFixed(2)}` : global.usd_inr_change.toFixed(2)) : null} invert />
              {/* Global sentiment badge */}
              <div style={{ padding: '0 10px', flexShrink: 0 }}>
                <span style={{
                  color: sentimentColor,
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  border: `1px solid ${sentimentColor}44`,
                  borderRadius: 10,
                  padding: '1px 6px',
                  textTransform: 'capitalize',
                }}>
                  ● {global.global_sentiment || 'neutral'}
                </span>
              </div>
            </>
          ) : (
            <span style={{ color: '#333', fontSize: '0.6rem', padding: '0 12px' }}>
              {loading ? 'Loading global markets...' : 'Global data unavailable'}
            </span>
          )}
        </div>

        {/* Right: refresh + add */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '0 8px', flexShrink: 0, borderLeft: '1px solid #1a1a2a' }}>
          <button
            onClick={fetchAll}
            disabled={loading}
            title="Refresh"
            style={{ background: 'none', border: 'none', color: '#444', cursor: 'pointer', padding: '3px 5px', borderRadius: 4, display: 'flex', alignItems: 'center' }}
          >
            <RefreshCw size={11} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          </button>
          <button
            onClick={() => setShowModal(true)}
            title="Add FII/DII/VIX data"
            style={{ background: 'rgba(38,166,91,0.15)', border: '1px solid #1a5c35', color: '#26a65b', cursor: 'pointer', padding: '2px 6px', borderRadius: 4, display: 'flex', alignItems: 'center' }}
          >
            <Plus size={11} />
          </button>
        </div>
      </div>

      {/* Add data modal */}
      {showModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{ background: '#0d0d1a', border: '1px solid #1a1a2e', borderRadius: 12, padding: '1.5rem', width: 460, maxHeight: '85vh', overflow: 'auto' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, color: '#ddd', fontSize: '1rem' }}>Add FII / DII / VIX Data</h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            <form onSubmit={handleSave}>
              <Field label="Date">
                <input type="date" name="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} required style={inp} />
              </Field>
              <div style={{ color: '#4fc3f7', fontSize: '0.75rem', fontWeight: 700, margin: '10px 0 6px' }}>FII (in Crores)</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                <Field label="Buy Value"><input type="number" step="0.01" placeholder="Buy" value={form.fii_buy_value} onChange={e => setForm(p => ({ ...p, fii_buy_value: e.target.value }))} required style={inp} /></Field>
                <Field label="Sell Value"><input type="number" step="0.01" placeholder="Sell" value={form.fii_sell_value} onChange={e => setForm(p => ({ ...p, fii_sell_value: e.target.value }))} required style={inp} /></Field>
              </div>
              <div style={{ color: '#26a65b', fontSize: '0.75rem', fontWeight: 700, margin: '10px 0 6px' }}>DII (in Crores)</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                <Field label="Buy Value"><input type="number" step="0.01" placeholder="Buy" value={form.dii_buy_value} onChange={e => setForm(p => ({ ...p, dii_buy_value: e.target.value }))} required style={inp} /></Field>
                <Field label="Sell Value"><input type="number" step="0.01" placeholder="Sell" value={form.dii_sell_value} onChange={e => setForm(p => ({ ...p, dii_sell_value: e.target.value }))} required style={inp} /></Field>
              </div>
              <div style={{ color: '#ce93d8', fontSize: '0.75rem', fontWeight: 700, margin: '10px 0 6px' }}>India VIX</div>
              <Field label="VIX Value"><input type="number" step="0.01" placeholder="e.g. 14.5" value={form.india_vix} onChange={e => setForm(p => ({ ...p, india_vix: e.target.value }))} required style={inp} /></Field>
              <p style={{ color: '#444', fontSize: '0.62rem', margin: '8px 0 16px' }}>Net, MTD, and previous day values are auto-calculated.</p>
              <button type="submit" disabled={saving} style={{ width: '100%', padding: '0.6rem', borderRadius: 8, border: 'none', background: saving ? '#333' : '#1a5c35', color: saving ? '#666' : '#26a65b', fontWeight: 700, fontSize: '0.85rem', cursor: saving ? 'not-allowed' : 'pointer' }}>
                {saving ? 'Saving...' : 'Save Market Data'}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <label style={{ color: '#666', fontSize: '0.68rem', display: 'block', marginBottom: 3 }}>{label}</label>
      {children}
    </div>
  );
}

const inp: React.CSSProperties = {
  width: '100%', padding: '6px 10px', borderRadius: 6,
  border: '1px solid #1a1a2e', background: '#0a0a14',
  color: '#ccc', fontSize: '0.82rem', boxSizing: 'border-box',
};

export default MarketInfoLayer;
